import { useChartTheme } from '@insolitum/ui/hooks/useChartTheme';
import { CATEGORY_SWATCHES } from '@insolitum/ui/theme/charts';
import {
  Building2,
  Network,
  Target,
  Save,
  Check,
  Edit3,
  Eye,
  LayoutGrid,
  List,
  X,
  Briefcase,
  Users,
  Sparkles,
  MapPin,
  FileText,
  Loader2,
  Zap,
  RefreshCw,
  TrendingUp,
  Heart,
  AlertTriangle,
  BarChart3,
  Activity,
  ShieldCheck,
  Lightbulb,
  Star,
  ThumbsUp,
  ThumbsDown,
  Minus,
  HelpCircle,
  Rocket,
  Factory,
  DollarSign,
  PieChart,
  Package,
  Globe,
  Calendar,
  Receipt,
  Wallet,
  Coins,
  Info,
  PlugZap,
  ArrowRight,
} from 'lucide-react';
import { useState, useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { OrgChart, OrgChartDiagram } from '../../components/OrgChart';
import {
  useCompanySalesMetrics,
  type CompanySalesMetrics,
  type CategorySlice,
} from '../../hooks/useCompanySalesMetrics';
import {
  useOrganizationalStructure,
  type UnitType,
  type OrgTreeNode,
  type CreateUnitInput,
} from '../../hooks/useOrganizationalStructure';
import {
  czyChronione,
  czyNieZbadano,
  dataZapisu,
  dopiszTylkoPuste,
  etykietaZrodla,
  wpisZrodla,
  type Zrodla,
} from '../../lib/pochodzeniePol';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../providers/AuthProvider';

// =============================================================================
// Pochodzenie pól (migracja 689, plan GENESIS_JEDNA_PRAWDA_PLAN.md §4.1).
//
// 13.09.2026 (Jan-Dar): ten ekran napisał researchem „firma handlowa, 10–50
// pracowników" o piekarni z 1500 osób, NADPISUJĄC to, co wywiad Genesis
// usłyszał, i pokazywał „Brak danych o produktach" tam, gdzie nikt produktów
// nie badał. Od teraz: każde pole niesie etykietę „skąd" (`ZrodloChip`), pole
// bez źródła i bez wartości mówi „nie zbadano" (`NieZbadano`) — nigdy
// „brak" — a „Odśwież research" dopisuje TYLKO pola puste i niechronione
// (`czyChronione`: wpis człowieka i wywiad zostają).
// =============================================================================

// ─── Types ──────────────────────────────────────────────────────────────────
interface StrategicPlan {
  type: string;
  title: string;
  priorities: string[];
  actions: Array<{ action: string; timeline: string; impact: string }>;
  kpis: string[];
}

interface ExtendedData {
  industry?: string;
  company_size?: string;
  products?: string;
  clients?: string;
  market_position?: string;
  customer_sentiment?: 'positive' | 'mixed' | 'negative' | 'unknown';
  customer_reviews_summary?: string;
  company_health?: 'strong' | 'stable' | 'needs_improvement' | 'critical' | 'unknown';
  company_health_analysis?: string;
  research_summary?: string;
  research_confidence?: 'high' | 'medium' | 'low';
  research_updated_at?: string;
  strategic_plan?: StrategicPlan;
}

/** Shape of the `applied` block returned by POST /api/nexus/company/research —
 *  od 13.09.2026 serwer zwraca w nim TYLKO pola dopisane (puste, niechronione)
 *  i osobno `zrodla` dla nich; jednostek organizacyjnych nie proponuje. */
interface ResearchApplied extends ExtendedData {
  mission?: string;
  goals?: string;
  differentiators?: string;
}

interface ResearchOutcome {
  confidence?: 'high' | 'medium' | 'low';
  summary?: string;
  fieldsUpdated: number;
  cached: boolean;
}

/** Keys copied verbatim from the research result into `extended_data`. */
const EXTENDED_RESEARCH_KEYS = [
  'industry',
  'company_size',
  'products',
  'clients',
  'market_position',
  'customer_sentiment',
  'customer_reviews_summary',
  'company_health',
  'company_health_analysis',
  'research_summary',
  'research_confidence',
  'strategic_plan',
] as const;

/** Pola tekstowe profilu, które research może dopisać — nigdy nadpisać. */
const TEKSTOWE_RESEARCH_KEYS = ['mission', 'goals', 'differentiators'] as const;

// ─── Unit Form Modal ──────────────────────────────────────────────────────
interface UnitFormProps {
  onClose: () => void;
  onSave: (data: CreateUnitInput) => void;
  editingUnit?: OrgTreeNode | null;
  parentId?: string | null;
  defaultType?: UnitType;
}

const UNIT_TYPE_OPTIONS: { value: UnitType; label: string; icon: typeof Building2 }[] = [
  { value: 'department', label: 'Dział', icon: Building2 },
  { value: 'position', label: 'Stanowisko', icon: Briefcase },
  { value: 'team', label: 'Zespół', icon: Users },
];

// Swatche koloru jednostki organizacyjnej — ta sama lista, co w pozostałych
// polach „wybierz kolor" w systemie (packages/ui/src/theme/charts.ts).
const COLORS = CATEGORY_SWATCHES;

/**
 * Add/edit dialog for a single org unit.
 *
 * IMPORTANT: this component must be mounted only while open, and remounted per
 * target (see the `key` on the call site). Its state is seeded from props with
 * `useState` initialisers, which run exactly once per mount — when it was
 * rendered unconditionally and self-hid with `if (!isOpen) return null`, every
 * "Edytuj" opened a form still holding the values from the first mount (i.e.
 * empty), and saving wrote those stale values over the unit's code, description
 * and colour.
 */
function UnitFormModal({ onClose, onSave, editingUnit, parentId, defaultType }: UnitFormProps) {
  const [name, setName] = useState(editingUnit?.name || '');
  const [unitType, setUnitType] = useState<UnitType>(
    editingUnit?.unit_type || defaultType || 'department'
  );
  const [code, setCode] = useState(editingUnit?.code || '');
  const [description, setDescription] = useState(editingUnit?.description || '');
  const [color, setColor] = useState(
    editingUnit?.color || COLORS[Math.floor(Math.random() * COLORS.length)]
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    onSave({
      parent_id: parentId,
      unit_type: unitType,
      name: name.trim(),
      code: code.trim() || undefined,
      description: description.trim() || undefined,
      color,
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-aurora-scrim" onClick={onClose} />
      <div className="relative bg-aurora-surface rounded-ids-xl border border-aurora-border shadow-elev-4 p-6 w-full max-w-md max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-5">
          <h3 className="display text-lg font-semibold text-aurora-text">
            {editingUnit ? 'Edytuj element' : 'Dodaj element'}
          </h3>
          <button onClick={onClose} className="iconbtn">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="field__label block mb-2">Typ</label>
            <div className="flex gap-2">
              {UNIT_TYPE_OPTIONS.map(({ value, label, icon: Icon }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setUnitType(value)}
                  aria-pressed={unitType === value}
                  className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-ids-md border text-sm transition-colors ${
                    unitType === value
                      ? 'bg-blue-500/20 border-aurora-brand text-aurora-brand'
                      : 'bg-aurora-bg-subtle border-aurora-border text-aurora-muted hover:border-aurora-border-strong'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div className="field">
            <label className="field__label">
              Nazwa <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
              required
              className="input text-sm"
              placeholder={
                unitType === 'department'
                  ? 'np. Produkcja'
                  : unitType === 'position'
                    ? 'np. Kierownik produkcji'
                    : 'np. Zespół jakości'
              }
            />
          </div>
          <div className="field">
            <label className="field__label">Kod (opcjonalny)</label>
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              className="input text-sm"
              placeholder="np. PROD, HR, IT"
            />
          </div>
          <div className="field">
            <label className="field__label">Opis</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className="textarea text-sm resize-none"
              placeholder="Opcjonalny opis..."
            />
          </div>
          <div>
            <label className="field__label block mb-2">Kolor</label>
            <div className="flex gap-2 flex-wrap">
              {COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setColor(c)}
                  className={`w-8 h-8 rounded-ids-md transition-all ${color === c ? 'ring-2 ring-white ring-offset-2 ring-offset-gray-800 scale-110' : 'hover:scale-105'}`}
                  style={{ backgroundColor: c }}
                />
              ))}
            </div>
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn btn--secondary flex-1">
              Anuluj
            </button>
            <button type="submit" disabled={!name.trim()} className="btn btn--primary flex-1">
              <Save className="w-4 h-4" />
              {editingUnit ? 'Zapisz' : 'Dodaj'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Health Badge ────────────────────────────────────────────────────────
function HealthBadge({ health }: { health?: string }) {
  const config = {
    strong: {
      label: 'Silna kondycja',
      color: 'text-emerald-400',
      bg: 'bg-emerald-500/15 border-emerald-500/30',
      icon: ShieldCheck,
    },
    stable: {
      label: 'Stabilna',
      color: 'text-blue-400',
      bg: 'bg-blue-500/15 border-blue-500/30',
      icon: Activity,
    },
    needs_improvement: {
      label: 'Wymaga poprawy',
      color: 'text-amber-400',
      bg: 'bg-amber-500/15 border-amber-500/30',
      icon: AlertTriangle,
    },
    critical: {
      label: 'Stan krytyczny',
      color: 'text-red-400',
      bg: 'bg-red-500/15 border-red-500/30',
      icon: AlertTriangle,
    },
    unknown: {
      label: 'Brak danych',
      color: 'text-aurora-muted',
      bg: 'bg-aurora-bg-subtle border-aurora-border',
      icon: HelpCircle,
    },
  };
  const c = config[health as keyof typeof config] || config.unknown;
  const Icon = c.icon;
  return (
    <div className={`pill border ${c.bg} ${c.color}`}>
      <Icon className="w-4 h-4" />
      {c.label}
    </div>
  );
}

// ─── Sentiment Badge ────────────────────────────────────────────────────
function SentimentBadge({ sentiment }: { sentiment?: string }) {
  const config = {
    positive: { label: 'Pozytywny', color: 'text-emerald-400', icon: ThumbsUp },
    mixed: { label: 'Mieszany', color: 'text-amber-400', icon: Minus },
    negative: { label: 'Negatywny', color: 'text-red-400', icon: ThumbsDown },
    unknown: { label: 'Brak danych', color: 'text-aurora-muted', icon: HelpCircle },
  };
  const c = config[sentiment as keyof typeof config] || config.unknown;
  const Icon = c.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 text-sm font-medium ${c.color}`}>
      <Icon className="w-4 h-4" />
      {c.label}
    </span>
  );
}

// ─── Sales charts (real data) ───────────────────────────────────────────
//
// These replaced four charts that rendered the same hard-coded array. Every
// number below comes from the organization's own POS rows via
// useCompanySalesMetrics; when there are none, SalesSection says so instead of
// drawing a shape.

const PLN = new Intl.NumberFormat('pl-PL', {
  style: 'currency',
  currency: 'PLN',
  maximumFractionDigits: 0,
});
const PLN_PRECISE = new Intl.NumberFormat('pl-PL', {
  style: 'currency',
  currency: 'PLN',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const COUNT = new Intl.NumberFormat('pl-PL');

function ChartCard({
  title,
  icon: Icon,
  color,
  caption,
  children,
}: {
  title: string;
  icon: typeof BarChart3;
  color: string;
  caption?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="card p-5">
      <div className="flex items-center justify-between mb-4 gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <Icon className="w-4 h-4 flex-shrink-0" style={{ color }} />
          <span className="text-sm font-medium text-aurora-text truncate">{title}</span>
        </div>
        {caption && (
          <span className="text-xs text-aurora-subtle tabular-nums flex-shrink-0">{caption}</span>
        )}
      </div>
      {children}
    </div>
  );
}

function BarSeries({
  points,
  color,
  format,
}: {
  points: Array<{ label: string; value: number; title: string }>;
  color: string;
  format: (v: number) => string;
}) {
  const max = Math.max(...points.map((p) => p.value), 0);
  return (
    <div className="flex items-end gap-1.5 h-28">
      {points.map((p, i) => (
        <div key={i} className="flex-1 flex flex-col items-center gap-1 group" title={p.title}>
          <div className="w-full flex-1 flex items-end">
            <div
              className="w-full rounded-t transition-all duration-500 group-hover:opacity-100 opacity-85"
              style={{
                height: max > 0 ? `${Math.max((p.value / max) * 100, 2)}%` : '2%',
                background: `linear-gradient(to top, ${color}33, ${color}CC)`,
              }}
            />
          </div>
          <span className="text-[9px] text-aurora-subtle">{p.label}</span>
        </div>
      ))}
      <span className="sr-only">{format(max)}</span>
    </div>
  );
}

function LineSeries({
  points,
  color,
  gradientId,
}: {
  points: Array<{ label: string; value: number; title: string }>;
  color: string;
  gradientId: string;
}) {
  const max = Math.max(...points.map((p) => p.value), 0);
  const min = Math.min(...points.map((p) => p.value), 0);
  const span = max - min || 1;
  const stepX = points.length > 1 ? 240 / (points.length - 1) : 0;
  const y = (v: number) => 100 - ((v - min) / span) * 90 - 5;
  const coords = points.map((p, i) => `${i * stepX},${y(p.value)}`);

  return (
    <div className="h-28 flex items-end">
      <svg viewBox="0 0 240 100" className="w-full h-full" preserveAspectRatio="none">
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.35" />
            <stop offset="100%" stopColor={color} stopOpacity="0.02" />
          </linearGradient>
        </defs>
        {points.length > 1 && (
          <path d={`M${coords.join(' L')} L240,100 L0,100 Z`} fill={`url(#${gradientId})`} />
        )}
        <polyline
          points={coords.join(' ')}
          fill="none"
          stroke={color}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          vectorEffect="non-scaling-stroke"
        />
        {points.map((p, i) => (
          <circle key={i} cx={i * stepX} cy={y(p.value)} r="2.5" fill={color}>
            <title>{p.title}</title>
          </circle>
        ))}
      </svg>
    </div>
  );
}

function CategoryDonut({ slices }: { slices: CategorySlice[] }) {
  // Wycinki pierścienia to serie wykresu, więc kolejność bierzemy z palety;
  // ostatni wycinek („pozostałe") jest neutralny, żeby nie udawał kategorii.
  // Wewnątrz komponentu, bo w trybie jasnym kotwica serii schodzi z 500 na 600.
  const chart = useChartTheme();
  const SLICE_COLORS = [...chart.series.slice(0, 5), chart.status.idle];

  const R = 40;
  const C = 2 * Math.PI * R;
  let cumulative = 0;
  const arcs = slices.map((slice, i) => {
    const dash = (slice.share / 100) * C;
    const offset = -(cumulative / 100) * C;
    cumulative += slice.share;
    return { slice, dash, offset, color: SLICE_COLORS[i % SLICE_COLORS.length] };
  });
  const top = slices[0];

  return (
    <div className="h-28 flex items-center gap-4">
      <svg viewBox="0 0 100 100" className="w-24 h-24 flex-shrink-0 -rotate-90">
        {/* Bieżnia pierścienia — atrybut prezentacyjny SVG jest mapowany na
            właściwość CSS, więc token działa i reaguje na data-mode. */}
        <circle cx="50" cy="50" r={R} fill="none" stroke="var(--ids-border)" strokeWidth="12" />
        {arcs.map(({ slice, dash, offset, color }) => (
          <circle
            key={slice.name}
            cx="50"
            cy="50"
            r={R}
            fill="none"
            stroke={color}
            strokeWidth="12"
            strokeDasharray={`${dash} ${C - dash}`}
            strokeDashoffset={offset}
          >
            <title>{`${slice.name}: ${PLN.format(slice.netSales)} (${slice.share.toFixed(1)}%)`}</title>
          </circle>
        ))}
        {top && (
          <text
            x="50"
            y="50"
            textAnchor="middle"
            dy=".3em"
            className="fill-white text-[11px] font-bold"
            transform="rotate(90 50 50)"
          >
            {Math.round(top.share)}%
          </text>
        )}
      </svg>
      <div className="space-y-1 min-w-0 overflow-hidden">
        {arcs.slice(0, 4).map(({ slice, color }) => (
          <div key={slice.name} className="flex items-center gap-2 text-xs text-aurora-muted">
            <div
              className="w-2.5 h-2.5 rounded-full flex-shrink-0"
              style={{ backgroundColor: color }}
            />
            <span className="truncate">{slice.name}</span>
            <span className="text-aurora-subtle tabular-nums flex-shrink-0">
              {slice.share.toFixed(0)}%
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function SalesSection({ metrics }: { metrics: CompanySalesMetrics }) {
  const { isLoading, error, hasData, monthly, categories, momChangePct, periodMonths } = metrics;

  if (isLoading) {
    return (
      <div className="card p-8 flex items-center justify-center gap-3 text-sm text-aurora-muted">
        <Loader2 className="w-4 h-4 animate-spin" />
        Wczytuję dane sprzedażowe...
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-500/5 border border-red-500/20 rounded-ids-xl p-5 flex items-start gap-3">
        <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
        <div>
          <p className="text-sm text-red-300 font-medium">Nie udało się wczytać danych sprzedaży</p>
          <p className="text-xs text-red-300/70 mt-1">{error}</p>
        </div>
      </div>
    );
  }

  if (!hasData) {
    // No POS integration for this organization. Saying that is the honest
    // answer; the previous build drew a fabricated 12-month trend here.
    return (
      <div className="card border-dashed p-8 text-center">
        <PlugZap className="w-8 h-8 text-aurora-subtle mx-auto mb-3" />
        <h3 className="text-sm font-semibold text-aurora-text mb-1">
          Brak podłączonego źródła sprzedaży
        </h3>
        <p className="text-xs text-aurora-subtle max-w-md mx-auto">
          Wskaźniki sprzedaży, liczby paragonów i struktury przychodów pojawią się tutaj
          automatycznie, gdy organizacja będzie miała skonfigurowaną integrację sprzedażową.
        </p>
      </div>
    );
  }

  const revenuePoints = monthly.map((m) => ({
    label: m.label,
    value: m.netSales,
    title: `${m.month}: ${PLN.format(m.netSales)}`,
  }));
  const receiptPoints = monthly.map((m) => ({
    label: m.label,
    value: m.receipts,
    title: `${m.month}: ${COUNT.format(m.receipts)} paragonów`,
  }));
  const basketPoints = monthly.map((m) => ({
    label: m.label,
    value: m.avgReceipt,
    title: `${m.month}: ${PLN_PRECISE.format(m.avgReceipt)}`,
  }));

  const period = `${monthly.length}/${periodMonths} mies.`;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
      <ChartCard
        title="Sprzedaż miesięczna"
        icon={DollarSign}
        color="#10B981"
        caption={
          momChangePct === null
            ? period
            : `${momChangePct >= 0 ? '+' : ''}${momChangePct.toFixed(1)}% m/m`
        }
      >
        <BarSeries points={revenuePoints} color="#10B981" format={PLN.format} />
      </ChartCard>

      <ChartCard title="Liczba paragonów" icon={Receipt} color="#3B82F6" caption={period}>
        <LineSeries points={receiptPoints} color="#3B82F6" gradientId="grad-receipts" />
      </ChartCard>

      <ChartCard
        title="Średni paragon"
        icon={Wallet}
        color="#F59E0B"
        caption={PLN_PRECISE.format(metrics.avgReceipt)}
      >
        <LineSeries points={basketPoints} color="#F59E0B" gradientId="grad-basket" />
      </ChartCard>

      <ChartCard
        title="Struktura przychodów"
        icon={PieChart}
        color="#06B6D4"
        caption={categories.length > 0 ? `${categories.length} kat.` : undefined}
      >
        {categories.length > 0 ? (
          <CategoryDonut slices={categories} />
        ) : (
          <div className="h-28 flex items-center justify-center text-xs text-aurora-subtle text-center px-2">
            Brak podziału na kategorie produktów
          </div>
        )}
      </ChartCard>
    </div>
  );
}

// ─── Impact Badge ───────────────────────────────────────────────────────
function ImpactBadge({ impact }: { impact: string }) {
  const c =
    impact === 'high'
      ? 'bg-red-500/15 text-red-400 border-red-500/30'
      : impact === 'medium'
        ? 'bg-amber-500/15 text-amber-400 border-amber-500/30'
        : 'bg-blue-500/15 text-blue-400 border-blue-500/30';
  return (
    <span className={`badge border ${c}`}>
      {impact === 'high' ? 'Wysoki' : impact === 'medium' ? 'Średni' : 'Niski'}
    </span>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────
export function CompanyProfilePage() {
  const { currentOrganization, hasRole } = useAuth();
  const {
    tree,
    units,
    profile,
    isLoading,
    createUnit,
    updateUnit,
    deleteUnit,
    countDescendants,
    saveProfile,
  } = useOrganizationalStructure();
  const salesMetrics = useCompanySalesMetrics();

  const [viewMode, setViewMode] = useState<'tree' | 'diagram'>('diagram');
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'structure' | 'strategy'>('overview');

  // Profile form
  const [editMission, setEditMission] = useState(profile?.mission || '');
  const [editGoals, setEditGoals] = useState(profile?.goals || '');
  const [editDifferentiators, setEditDifferentiators] = useState(profile?.differentiators || '');
  const [editAnalysis, setEditAnalysis] = useState(profile?.analysis || '');
  const [editPolicies, setEditPolicies] = useState(profile?.policies || '');
  const [editAssets, setEditAssets] = useState(profile?.assets_description || '');
  const [editCostStructure, setEditCostStructure] = useState(profile?.cost_structure || '');
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [profileSuccess, setProfileSuccess] = useState(false);

  // Unit form modal
  const [showUnitForm, setShowUnitForm] = useState(false);
  const [editingUnit, setEditingUnit] = useState<OrgTreeNode | null>(null);
  const [addParentId, setAddParentId] = useState<string | null>(null);
  const [addDefaultType, setAddDefaultType] = useState<UnitType>('department');

  // NEXUS research
  const [isResearching, setIsResearching] = useState(false);
  const [researchOutcome, setResearchOutcome] = useState<ResearchOutcome | null>(null);
  const [researchError, setResearchError] = useState<string | null>(null);
  const navigate = useNavigate();

  const canEdit = hasRole(['owner', 'admin']);
  const org = currentOrganization?.organization;
  const ext: ExtendedData = (profile?.extended_data || {}) as ExtendedData;
  const zrodla: Zrodla = profile?.zrodla ?? {};

  const hasEmptyProfile = !profile?.mission && !profile?.goals && !profile?.differentiators;
  const hasEmptyStructure = units.length === 0;
  const needsEnrichment = hasEmptyProfile || hasEmptyStructure;
  const hasResearch = !!ext.research_confidence;

  /**
   * Run company research and persist everything it returns.
   *
   * The previous implementation POSTed to the onboarding CHAT endpoint, which
   * starts research in the background and answers with a conversational reply.
   * Because `extracted_data` is always at least `{}` (truthy), the page took the
   * success branch every time and painted "Research zakończony" over a request
   * that had researched nothing; and even on a cache hit it only ever mapped
   * mission/goals/differentiators, never `extended_data` — which is why the
   * health, sentiment and strategic-plan cards stayed empty forever.
   */
  const handleNexusResearch = useCallback(
    async (options: { force?: boolean } = {}) => {
      if (!org?.id) return;
      setIsResearching(true);
      setResearchError(null);
      setResearchOutcome(null);

      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        const token = session?.access_token;
        if (!token) throw new Error('Brak autoryzacji — zaloguj się ponownie.');

        const NEXUS_API_URL = import.meta.env.VITE_NEXUS_API_URL || '/nexus-api';
        const response = await fetch(`${NEXUS_API_URL}/api/nexus/company/research`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ organization_id: org.id, force: options.force === true }),
        });

        if (response.status === 404) {
          throw new Error(
            'Usługa researchu NEXUS jeszcze nie odpowiada pod tym adresem. Spróbuj ponownie za kilka minut.'
          );
        }
        if (!response.ok) {
          const body = (await response.json().catch(() => null)) as { message?: string } | null;
          throw new Error(body?.message || `Research nie powiódł się (HTTP ${response.status}).`);
        }

        const payload = (await response.json()) as {
          applied?: ResearchApplied;
          zrodla?: Zrodla;
          cached?: boolean;
          confidence?: 'high' | 'medium' | 'low';
          summary?: string;
        };
        const appliedTyped: ResearchApplied = payload.applied || {};
        const applied = appliedTyped as Record<string, unknown>;
        const biezaceZrodla: Zrodla = profile?.zrodla ?? {};

        // Research DOPISUJE, nigdy nie nadpisuje: pole wchodzi tylko, gdy jest
        // puste i nie jest chronione (`czlowiek`/`wywiad`). Serwer robi to samo
        // po swojej stronie; tu jest druga bramka, bo to ten ekran 13.09.2026
        // zamienił piekarnię w firmę handlową.
        const ekstra = dopiszTylkoPuste(
          (profile?.extended_data || {}) as Record<string, unknown>,
          biezaceZrodla,
          applied,
          EXTENDED_RESEARCH_KEYS
        );
        const tekstowe = dopiszTylkoPuste(
          {
            mission: profile?.mission ?? null,
            goals: profile?.goals ?? null,
            differentiators: profile?.differentiators ?? null,
          },
          biezaceZrodla,
          applied,
          TEKSTOWE_RESEARCH_KEYS
        );
        const dopisane = [...ekstra.dopisane, ...tekstowe.dopisane].filter(
          (pole) => !czyChronione(biezaceZrodla, pole)
        );
        const nextExtended: Record<string, unknown> = { ...ekstra.wynik };
        nextExtended.research_updated_at = new Date().toISOString();

        // Pochodzenie TYLKO dla pól, które faktycznie weszły — reszta `zrodla`
        // z serwera dotyczy pól, których nie tknęliśmy.
        const nextZrodla: Zrodla = { ...biezaceZrodla };
        for (const pole of dopisane) {
          const wpis = wpisZrodla(payload.zrodla, pole);
          if (wpis) nextZrodla[pole] = wpis;
        }

        const saved = await saveProfile({
          mission: (tekstowe.wynik.mission as string | null) ?? null,
          goals: (tekstowe.wynik.goals as string | null) ?? null,
          differentiators: (tekstowe.wynik.differentiators as string | null) ?? null,
          analysis: profile?.analysis ?? null,
          policies: profile?.policies ?? null,
          assets_description: profile?.assets_description ?? null,
          cost_structure: profile?.cost_structure ?? null,
          extended_data: nextExtended,
          zrodla: nextZrodla,
        });

        if (!saved) throw new Error('Research się udał, ale zapis profilu nie powiódł się.');
        const fieldsUpdated = dopisane.length;

        setResearchOutcome({
          confidence: payload.confidence || appliedTyped.research_confidence,
          summary: payload.summary || appliedTyped.research_summary,
          fieldsUpdated,
          cached: payload.cached === true,
        });
      } catch (err) {
        setResearchError((err as Error).message || 'Nie udało się przeprowadzić researchu.');
      } finally {
        setIsResearching(false);
      }
    },
    [org, profile, saveProfile]
  );

  // Sync profile form when profile loads
  useEffect(() => {
    if (profile) {
      setEditMission(profile.mission || '');
      setEditGoals(profile.goals || '');
      setEditDifferentiators(profile.differentiators || '');
      setEditAnalysis(profile.analysis || '');
      setEditPolicies(profile.policies || '');
      setEditAssets(profile.assets_description || '');
      setEditCostStructure(profile.cost_structure || '');
    }
  }, [profile]);

  const handleSaveProfile = async () => {
    setIsSavingProfile(true);
    // Zapis z formularza: zmienione pola dostają `zrodla[pole] = czlowiek`
    // i od tej chwili żaden research ich nie nadpisze.
    const success = await saveProfile(
      {
        mission: editMission.trim() || null,
        goals: editGoals.trim() || null,
        differentiators: editDifferentiators.trim() || null,
        analysis: editAnalysis.trim() || null,
        policies: editPolicies.trim() || null,
        assets_description: editAssets.trim() || null,
        cost_structure: editCostStructure.trim() || null,
      },
      { zrodloZmian: 'czlowiek' }
    );
    setIsSavingProfile(false);
    if (success) {
      setProfileSuccess(true);
      setIsEditing(false);
      setTimeout(() => setProfileSuccess(false), 3000);
    }
  };

  const handleAddUnit = useCallback((parentId: string | null, type: UnitType) => {
    setAddParentId(parentId);
    setAddDefaultType(type);
    setEditingUnit(null);
    setShowUnitForm(true);
  }, []);
  const handleEditUnit = useCallback((unit: OrgTreeNode) => {
    setEditingUnit(unit);
    setAddParentId(unit.parent_id);
    setShowUnitForm(true);
  }, []);
  const handleDeleteUnit = useCallback(
    async (unitId: string) => {
      const descendants = countDescendants(unitId);
      const message =
        descendants > 0
          ? `Usunąć ten element? Jego ${descendants} ${descendants === 1 ? 'podelement zostanie podpięty' : 'podelementów zostanie podpiętych'} pod nadrzędny poziom.`
          : 'Czy na pewno chcesz usunąć ten element?';
      if (confirm(message)) await deleteUnit(unitId);
    },
    [deleteUnit, countDescendants]
  );
  const handleSaveUnit = useCallback(
    async (data: CreateUnitInput) => {
      if (editingUnit) {
        await updateUnit(editingUnit.id, {
          name: data.name,
          code: data.code,
          description: data.description,
          color: data.color,
          parent_id: data.parent_id,
        });
      } else {
        await createUnit(data);
      }
    },
    [editingUnit, createUnit, updateUnit]
  );

  /**
   * Kreator w trybie aktualizacji ODNAJDUJE jednostki po nazwie i rodzaju
   * (ich `id` przeżywa, więc przeżywają przypisania pracowników i sprzętu),
   * a kasuje tylko te, których w nowej strukturze nie ma — wraz z ich
   * przypisaniami. Ostrzeżenie mówi dokładnie to, bo do 13.09.2026 straszyło
   * kasowaniem wszystkiego, czego kreator od dawna nie robił.
   */
  const handleFullOnboarding = useCallback(() => {
    if (units.length > 0) {
      const confirmed = confirm(
        `Pełny onboarding NEXUS ułoży strukturę organizacyjną na nowo.\n\n` +
          `Elementy o tej samej nazwie i rodzaju zostaną zachowane (${units.length} obecnych); ` +
          `te, których w nowej strukturze nie będzie, zostaną usunięte razem z przypisaniami pracowników i sprzętu.\n\n` +
          `Kontynuować?`
      );
      if (!confirmed) return;
    }
    navigate('/onboarding');
  }, [units.length, navigate]);

  /**
   * Concrete gaps in this organization's profile, derived from actual state.
   * Replaces a hard-coded list of four growth slogans ("Automatyzacja procesów
   * produkcyjnych", ...) that was picked by a ternary on `ext.products` and, being
   * nested inside the strategic-plan block, never rendered at all.
   */
  const nextSteps = useMemo(() => {
    const steps: Array<{ text: string; icon: typeof Target; onClick?: () => void }> = [];
    if (!hasResearch) {
      steps.push({
        text: 'Uruchom research NEXUS, aby poznać kondycję firmy i plan strategiczny',
        icon: Sparkles,
        onClick: canEdit ? () => handleNexusResearch() : undefined,
      });
    }
    if (hasEmptyStructure) {
      steps.push({
        text: 'Zbuduj strukturę organizacyjną — działy, stanowiska, zespoły',
        icon: Network,
        onClick: () => setActiveTab('structure'),
      });
    } else if (units.every((u) => (u.employee_count ?? 0) === 0)) {
      steps.push({
        text: 'Przypisz pracowników do jednostek organizacyjnych',
        icon: Users,
        onClick: () => navigate('/admin/workers'),
      });
    }
    if (hasEmptyProfile) {
      steps.push({
        text: 'Uzupełnij profil strategiczny — misję, cele i wyróżniki',
        icon: Target,
        onClick: () => setActiveTab('strategy'),
      });
    }
    if (!profile?.cost_structure || !profile?.assets_description) {
      steps.push({
        text: 'Opisz strukturę kosztów i kluczowe zasoby firmy',
        icon: Coins,
        onClick: () => {
          setActiveTab('strategy');
          setIsEditing(true);
        },
      });
    }
    if (!org?.nip) {
      steps.push({
        text: 'Uzupełnij NIP — bez niego research korzysta tylko z nazwy firmy',
        icon: FileText,
        onClick: () => navigate('/admin/settings'),
      });
    }
    if (!salesMetrics.isLoading && !salesMetrics.hasData) {
      steps.push({ text: 'Podłącz źródło danych sprzedażowych', icon: PlugZap });
    }
    return steps;
  }, [
    hasResearch,
    hasEmptyStructure,
    hasEmptyProfile,
    units,
    profile,
    org,
    salesMetrics.isLoading,
    salesMetrics.hasData,
    canEdit,
    handleNexusResearch,
    navigate,
  ]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-aurora-brand border-t-transparent rounded-full" />
      </div>
    );
  }

  const strategicPlan = ext.strategic_plan;
  const researchedAt = ext.research_updated_at ? new Date(ext.research_updated_at) : null;

  return (
    <div className="space-y-5">
      {/* Header — full-width hero card */}
      <div className="relative overflow-hidden card rounded-ids-xl p-6">
        {/* Background decoration */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-purple-500/5 rounded-full blur-3xl translate-y-1/2 -translate-x-1/3" />

        <div className="relative flex flex-col lg:flex-row lg:items-center gap-6">
          {/* Logo */}
          <div className="w-20 h-20 rounded-ids-lg bg-gradient-to-br from-blue-500/20 to-purple-500/20 border border-aurora-border flex items-center justify-center flex-shrink-0">
            <Building2 className="w-10 h-10 text-aurora-brand" />
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-3 mb-2">
              <h1 className="display text-2xl font-bold text-aurora-text">{org?.name}</h1>
              <span
                className={`pill ${
                  org?.status === 'active'
                    ? 'pill--active'
                    : org?.status === 'trial'
                      ? 'pill--trial'
                      : 'pill--error'
                }`}
              >
                <span className="pin" />
                {org?.status === 'active'
                  ? 'Aktywna'
                  : org?.status === 'trial'
                    ? 'Trial'
                    : org?.status}
              </span>
              <span className="badge badge--brand capitalize">{org?.subscription_plan}</span>
              {ext.industry && (
                <span
                  className="badge bg-purple-500/15 text-purple-400 border-transparent"
                  title={tytulZrodla(zrodla, 'industry')}
                >
                  <Factory className="w-3 h-3" />
                  {ext.industry}
                </span>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-sm text-aurora-muted">
              {org?.nip && (
                <span className="flex items-center gap-1.5">
                  <FileText className="w-3.5 h-3.5" />
                  NIP: <span className="mono">{org.nip}</span>
                </span>
              )}
              {org?.address && (
                <span className="flex items-center gap-1.5">
                  <MapPin className="w-3.5 h-3.5" />
                  {org.address}, {org.postal_code} {org.city}
                </span>
              )}
              {ext.company_size && (
                <span
                  className="flex items-center gap-1.5"
                  title={tytulZrodla(zrodla, 'company_size')}
                >
                  <Users className="w-3.5 h-3.5" />
                  {ext.company_size}
                </span>
              )}
              {canEdit && (
                <button
                  onClick={() => navigate('/admin/settings')}
                  className="flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300 transition-colors"
                >
                  <Edit3 className="w-3 h-3" />
                  Edytuj dane firmy
                </button>
              )}
            </div>
          </div>

          {/* Quick Stats */}
          <div className="flex gap-4 lg:gap-6 flex-shrink-0">
            <QuickStat
              label="Działy"
              value={units.filter((u) => u.unit_type === 'department').length}
              icon={Building2}
              color="text-blue-400"
            />
            <QuickStat
              label="Stanowiska"
              value={units.filter((u) => u.unit_type === 'position').length}
              icon={Briefcase}
              color="text-purple-400"
            />
            <QuickStat
              label="Zespoły"
              value={units.filter((u) => u.unit_type === 'team').length}
              icon={Users}
              color="text-emerald-400"
            />
          </div>
        </div>
      </div>

      {/* Success toast */}
      {profileSuccess && (
        <div className="flex items-center gap-2 p-3 bg-green-500/20 border border-green-500/30 rounded-ids-md text-green-400 text-sm">
          <Check className="w-4 h-4" />
          Profil strategiczny został zapisany
        </div>
      )}

      {/* Tab Navigation */}
      <div className="tabs overflow-x-auto">
        {(
          [
            { id: 'overview', label: 'Przegląd i analiza', icon: Activity },
            { id: 'structure', label: 'Struktura organizacyjna', icon: Network },
            { id: 'strategy', label: 'Profil strategiczny', icon: Target },
          ] as const
        ).map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            aria-selected={activeTab === tab.id}
            className="flex-1 flex items-center justify-center gap-2 whitespace-nowrap"
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* ═══════════════════════════════════════════════════════════════════════
           TAB: OVERVIEW & ANALYSIS
         ═══════════════════════════════════════════════════════════════════════ */}
      {activeTab === 'overview' && (
        <div className="space-y-5">
          {/* Top Row: Health + Sentiment + Research CTA */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            {/* Kondycja firmy — WYŁĄCZNIE gdy ktoś ją zbadał (wpis w `zrodla`).
                Sekcja z tekstem zastępczym byłaby werdyktem tam, gdzie jest niewiedza. */}
            {(wpisZrodla(zrodla, 'company_health') ||
              wpisZrodla(zrodla, 'company_health_analysis')) && (
              <div className="card p-5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="card__title text-sm flex items-center gap-2">
                    <Activity className="w-4 h-4 text-emerald-400" />
                    Kondycja firmy
                  </h3>
                  <HealthBadge health={ext.company_health} />
                </div>
                <PoleTekstowe
                  zrodla={zrodla}
                  pole="company_health_analysis"
                  wartosc={ext.company_health_analysis}
                />
              </div>
            )}

            {/* Jak nas widzą klienci — jak wyżej: ukryte bez źródła (§8.2 planu —
                opinie klientów czekają na decyzję; do tego czasu nie „brak opinii"). */}
            {(wpisZrodla(zrodla, 'customer_reviews_summary') ||
              wpisZrodla(zrodla, 'customer_sentiment')) && (
              <div className="card p-5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="card__title text-sm flex items-center gap-2">
                    <Heart className="w-4 h-4 text-pink-400" />
                    Jak nas widzą klienci
                  </h3>
                  <SentimentBadge sentiment={ext.customer_sentiment} />
                </div>
                <PoleTekstowe
                  zrodla={zrodla}
                  pole="customer_reviews_summary"
                  wartosc={ext.customer_reviews_summary}
                />
              </div>
            )}

            {/* NEXUS CTA */}
            <div className="bg-gradient-to-br from-purple-500/10 via-blue-500/5 to-indigo-500/10 border border-purple-500/20 rounded-ids-xl p-5 flex flex-col">
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <Sparkles className="w-5 h-5 text-purple-400" />
                <span className="card__title text-sm">NEXUS AI Research</span>
                {ext.research_confidence && (
                  <span
                    className={`badge border-transparent ${
                      ext.research_confidence === 'high'
                        ? 'bg-emerald-500/20 text-emerald-400'
                        : ext.research_confidence === 'medium'
                          ? 'bg-amber-500/20 text-amber-400'
                          : 'bg-aurora-bg-subtle text-aurora-muted'
                    }`}
                  >
                    {ext.research_confidence === 'high'
                      ? 'dane potwierdzone'
                      : ext.research_confidence === 'medium'
                        ? 'propozycje AI'
                        : 'wstępne'}
                  </span>
                )}
              </div>
              <p className="text-xs text-aurora-muted mb-2 flex-1">
                {ext.research_summary ||
                  'Głęboka analiza AI Twojej firmy — kondycja, klienci, rynek, rekomendacje strategiczne.'}
              </p>
              {researchedAt && (
                <p className="text-[10px] text-aurora-subtle mb-3 flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  Ostatni research: {researchedAt.toLocaleString('pl-PL')}
                </p>
              )}

              {researchOutcome && (
                <div className="mb-3 p-2.5 bg-emerald-500/10 border border-emerald-500/20 rounded-ids-md text-xs text-emerald-300 space-y-1">
                  <div className="flex items-center gap-1 font-medium">
                    <Check className="w-3 h-3" />
                    Dopisano {researchOutcome.fieldsUpdated}{' '}
                    {researchOutcome.fieldsUpdated === 1
                      ? 'puste pole'
                      : researchOutcome.fieldsUpdated >= 2 && researchOutcome.fieldsUpdated <= 4
                        ? 'puste pola'
                        : 'pustych pól'}
                    {researchOutcome.cached && ' (z pamięci podręcznej)'}
                  </div>
                  {researchOutcome.confidence === 'low' && (
                    <div className="flex items-start gap-1 text-amber-300">
                      <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
                      NEXUS nie rozpoznał tej firmy — analityczne oceny zostały pominięte, żeby nie
                      podawać zmyślonych danych.
                    </div>
                  )}
                  {researchOutcome.summary && (
                    <p className="text-emerald-200/70">{researchOutcome.summary}</p>
                  )}
                </div>
              )}
              {researchError && (
                <div className="mb-3 p-2.5 bg-red-500/10 border border-red-500/20 rounded-ids-md text-xs text-red-300">
                  {researchError}
                </div>
              )}

              <div className="flex flex-col gap-2">
                {canEdit && (
                  <button
                    onClick={() => handleNexusResearch({ force: hasResearch })}
                    disabled={isResearching}
                    className="btn btn--block bg-purple-600 hover:bg-purple-500 disabled:bg-purple-600/50 text-aurora-on-accent disabled:cursor-wait"
                  >
                    {isResearching ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        NEXUS analizuje...
                      </>
                    ) : hasResearch ? (
                      <>
                        <RefreshCw className="w-4 h-4" />
                        Odśwież research
                      </>
                    ) : (
                      <>
                        <Zap className="w-4 h-4" />
                        {needsEnrichment ? 'Uzupełnij dane z NEXUS' : 'Uruchom research AI'}
                      </>
                    )}
                  </button>
                )}
                {canEdit && (
                  <p className="text-[11px] leading-snug text-aurora-subtle">
                    Dopisuje tylko puste pola; Twoje wpisy i wywiad zostają.
                  </p>
                )}
                <button
                  onClick={handleFullOnboarding}
                  className="btn btn--sm btn--block bg-white/5 hover:bg-white/10 text-aurora-on-accent/70 hover:text-aurora-on-accent border-white/10 text-xs"
                >
                  <Sparkles className="w-3.5 h-3.5" />
                  Pełny onboarding z NEXUS
                </button>
              </div>
            </div>
          </div>

          {/* Sales metrics — real data, or an honest empty state */}
          <SalesSection metrics={salesMetrics} />

          {/* Bottom Row: Market Position + Products + Clients */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            {/* Market Position */}
            <div className="card p-5">
              <h3 className="card__title text-sm flex items-center gap-2 mb-3">
                <Globe className="w-4 h-4 text-cyan-400" />
                Pozycja rynkowa
              </h3>
              <PoleTekstowe zrodla={zrodla} pole="market_position" wartosc={ext.market_position} />
            </div>

            {/* Products */}
            <div className="card p-5">
              <h3 className="card__title text-sm flex items-center gap-2 mb-3">
                <Package className="w-4 h-4 text-amber-400" />
                Produkty i usługi
              </h3>
              <PoleTekstowe zrodla={zrodla} pole="products" wartosc={ext.products} />
            </div>

            {/* Clients */}
            <div className="card p-5">
              <h3 className="card__title text-sm flex items-center gap-2 mb-3">
                <Users className="w-4 h-4 text-blue-400" />
                Klienci
              </h3>
              <PoleTekstowe zrodla={zrodla} pole="clients" wartosc={ext.clients} />
            </div>
          </div>

          {/* Strategic Plan + next steps */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {strategicPlan ? (
              <div className="card p-5">
                <h3 className="card__title text-sm flex items-center gap-2 mb-1">
                  <Rocket className="w-4 h-4 text-orange-400" />
                  {strategicPlan.title || 'Plan strategiczny'}
                  <ZrodloChip zrodla={zrodla} pole="strategic_plan" inline />
                </h3>
                <p className="text-xs text-gray-500 mb-4">
                  {strategicPlan.type === 'growth'
                    ? 'Plan wzrostu'
                    : strategicPlan.type === 'turnaround'
                      ? 'Plan naprawczy'
                      : strategicPlan.type === 'improvement'
                        ? 'Plan doskonalenia'
                        : 'Plan utrzymania pozycji'}
                </p>

                {strategicPlan.priorities?.length > 0 && (
                  <div className="mb-4">
                    <p className="overline mb-2">Priorytety</p>
                    <div className="space-y-1.5">
                      {strategicPlan.priorities.map((p, i) => (
                        <div key={i} className="flex items-start gap-2">
                          <span className="w-5 h-5 rounded-full bg-orange-500/20 text-orange-400 text-[10px] font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                            {i + 1}
                          </span>
                          <span className="text-sm text-aurora-text">{p}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {strategicPlan.actions?.length > 0 && (
                  <div>
                    <p className="overline mb-2">Działania</p>
                    <div className="space-y-2">
                      {strategicPlan.actions.map((a, i) => (
                        <div key={i} className="bg-aurora-bg-subtle rounded-ids-md p-3">
                          <div className="flex items-start justify-between gap-2">
                            <span className="text-sm text-aurora-text">{a.action}</span>
                            <ImpactBadge impact={a.impact} />
                          </div>
                          {a.timeline && (
                            <p className="text-xs text-aurora-subtle mt-1 flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {a.timeline}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-gradient-to-br from-indigo-500/5 to-purple-500/5 border border-indigo-500/20 rounded-ids-xl p-8 text-center flex flex-col items-center justify-center">
                <Lightbulb className="w-10 h-10 text-yellow-400/60 mb-3" />
                <h3 className="display text-lg font-semibold text-aurora-text mb-2">
                  Strategia i plan rozwoju
                </h3>
                <p className="text-sm text-aurora-muted mb-4 max-w-md">
                  Uruchom NEXUS AI Research, aby otrzymać pełną analizę strategiczną — plan
                  działania, priorytety, KPI i rekomendacje rozwoju.
                </p>
                {canEdit && (
                  <button
                    onClick={() => handleNexusResearch({ force: hasResearch })}
                    disabled={isResearching}
                    className="btn bg-purple-600 hover:bg-purple-500 disabled:bg-purple-600/50 text-aurora-on-accent disabled:cursor-wait"
                  >
                    {isResearching ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Sparkles className="w-4 h-4" />
                    )}
                    Przeprowadź analizę AI
                  </button>
                )}
              </div>
            )}

            <div className="space-y-5">
              {strategicPlan && strategicPlan.kpis?.length > 0 && (
                <div className="card p-5">
                  <h3 className="card__title text-sm flex items-center gap-2 mb-3">
                    <BarChart3 className="w-4 h-4 text-cyan-400" />
                    KPI do monitorowania
                  </h3>
                  <div className="space-y-2">
                    {strategicPlan.kpis.map((kpi, i) => (
                      <div
                        key={i}
                        className="flex items-center gap-3 bg-aurora-bg-subtle rounded-ids-md px-3 py-2.5"
                      >
                        <div className="w-2 h-2 rounded-full bg-cyan-400 flex-shrink-0" />
                        <span className="text-sm text-gray-300">{kpi}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Next steps — computed from what this profile is actually missing */}
              {nextSteps.length > 0 && (
                <div className="bg-gradient-to-br from-indigo-500/10 to-purple-500/10 border border-indigo-500/20 rounded-ids-xl p-5">
                  <h3 className="card__title text-sm flex items-center gap-2 mb-3">
                    <Lightbulb className="w-4 h-4 text-yellow-400" />
                    Co jeszcze warto uzupełnić
                  </h3>
                  <div className="space-y-2">
                    {nextSteps.map((step, i) => {
                      const Icon = step.icon;
                      const interactive = !!step.onClick;
                      return (
                        <button
                          key={i}
                          onClick={step.onClick}
                          disabled={!interactive}
                          className={`w-full flex items-center gap-3 text-sm text-aurora-text bg-white/5 rounded-ids-md px-3 py-2.5 text-left transition-colors ${
                            interactive
                              ? 'hover:bg-white/10 cursor-pointer'
                              : 'cursor-default opacity-80'
                          }`}
                        >
                          <Icon className="w-4 h-4 text-indigo-400 flex-shrink-0" />
                          <span className="flex-1">{step.text}</span>
                          {interactive && (
                            <ArrowRight className="w-3.5 h-3.5 text-aurora-subtle flex-shrink-0" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {nextSteps.length === 0 && salesMetrics.hasData && (
                <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-ids-xl p-5 flex items-center gap-3">
                  <TrendingUp className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                  <p className="text-sm text-aurora-text">
                    Profil firmy jest kompletny — wszystkie sekcje mają dane.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════════
           TAB: ORGANIZATIONAL STRUCTURE
         ═══════════════════════════════════════════════════════════════════════ */}
      {activeTab === 'structure' && (
        <div className="card p-6">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
            <h2 className="card__title text-lg flex items-center gap-2">
              <Network className="w-5 h-5 text-emerald-400" />
              Struktura organizacyjna
            </h2>
            <div className="flex items-center gap-2">
              {canEdit && viewMode === 'diagram' && tree.length > 0 && (
                <span className="hidden sm:flex items-center gap-1 text-xs text-aurora-subtle">
                  <Info className="w-3 h-3" />
                  Edycja elementów w widoku listy
                </span>
              )}
              <div className="segmented" role="group" aria-label="Widok struktury">
                <button
                  onClick={() => setViewMode('diagram')}
                  aria-pressed={viewMode === 'diagram'}
                  className="flex items-center gap-1.5"
                >
                  <LayoutGrid className="w-3.5 h-3.5" />
                  Diagram
                </button>
                <button
                  onClick={() => setViewMode('tree')}
                  aria-pressed={viewMode === 'tree'}
                  className="flex items-center gap-1.5"
                >
                  <List className="w-3.5 h-3.5" />
                  Lista
                </button>
              </div>
            </div>
          </div>

          {viewMode === 'diagram' ? (
            <OrgChartDiagram
              tree={tree}
              organizationName={org?.name}
              editable={canEdit}
              onAddUnit={handleAddUnit}
            />
          ) : (
            <OrgChart
              tree={tree}
              organizationName={org?.name}
              editable={canEdit}
              onAddUnit={handleAddUnit}
              onEditUnit={handleEditUnit}
              onDeleteUnit={handleDeleteUnit}
            />
          )}

          <div className="flex flex-wrap items-center gap-4 mt-6 pt-4 border-t border-aurora-border">
            <span className="text-xs text-aurora-subtle">Legenda:</span>
            <span className="flex items-center gap-1.5 text-xs text-aurora-muted">
              <div className="w-3 h-3 rounded bg-blue-500/30 border border-blue-500/50" />
              Dział
            </span>
            <span className="flex items-center gap-1.5 text-xs text-aurora-muted">
              <div className="w-3 h-3 rounded bg-purple-500/30 border border-purple-500/50" />
              Stanowisko
            </span>
            <span className="flex items-center gap-1.5 text-xs text-aurora-muted">
              <div className="w-3 h-3 rounded bg-emerald-500/30 border border-emerald-500/50" />
              Zespół
            </span>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════════
           TAB: STRATEGIC PROFILE
         ═══════════════════════════════════════════════════════════════════════ */}
      {activeTab === 'strategy' && (
        <div className="card p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="card__title text-lg flex items-center gap-2">
              <Target className="w-5 h-5 text-purple-400" />
              Profil strategiczny
            </h2>
            {canEdit && (
              <button
                onClick={() => setIsEditing(!isEditing)}
                className={`btn btn--sm ${isEditing ? 'btn--tonal' : 'btn--ghost'}`}
              >
                {isEditing ? (
                  <>
                    <Eye className="w-4 h-4" />
                    Podgląd
                  </>
                ) : (
                  <>
                    <Edit3 className="w-4 h-4" />
                    Edytuj
                  </>
                )}
              </button>
            )}
          </div>

          {isEditing ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              <ProfileField
                label="Misja"
                value={editMission}
                onChange={setEditMission}
                placeholder="Misja firmy..."
              />
              <ProfileField
                label="Cele strategiczne"
                value={editGoals}
                onChange={setEditGoals}
                placeholder="Cele strategiczne..."
              />
              <ProfileField
                label="Wyróżniki"
                value={editDifferentiators}
                onChange={setEditDifferentiators}
                placeholder="Przewaga konkurencyjna..."
              />
              <ProfileField
                label="Analiza"
                value={editAnalysis}
                onChange={setEditAnalysis}
                placeholder="Analiza SWOT, rynkowa..."
              />
              <ProfileField
                label="Polityka"
                value={editPolicies}
                onChange={setEditPolicies}
                placeholder="Polityki organizacyjne..."
              />
              <ProfileField
                label="Zasoby"
                value={editAssets}
                onChange={setEditAssets}
                placeholder="Opis zasobów firmy..."
              />
              {/* Koszta — the column and the state existed all along, but no input
                  was ever rendered for it, so the field was unreachable. */}
              <ProfileField
                label="Struktura kosztów"
                value={editCostStructure}
                onChange={setEditCostStructure}
                placeholder="Główne kategorie kosztów, koszty stałe i zmienne..."
              />
              <div className="lg:col-span-2">
                <button
                  onClick={handleSaveProfile}
                  disabled={isSavingProfile}
                  className="btn btn--primary"
                >
                  <Save className="w-4 h-4" />
                  {isSavingProfile ? 'Zapisywanie...' : 'Zapisz profil strategiczny'}
                </button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {!profile?.mission &&
              !profile?.goals &&
              !profile?.differentiators &&
              !profile?.analysis &&
              !profile?.policies &&
              !profile?.assets_description &&
              !profile?.cost_structure ? (
                <div className="lg:col-span-2 text-center py-12">
                  <Target className="w-10 h-10 text-aurora-subtle mx-auto mb-3" />
                  <p className="text-sm text-aurora-muted mb-3">
                    Profil strategiczny — <NieZbadano inline />
                  </p>
                  {canEdit && (
                    <div className="flex flex-wrap items-center justify-center gap-3">
                      <button
                        onClick={() => setIsEditing(true)}
                        className="text-sm text-aurora-brand hover:text-blue-300 flex items-center gap-1"
                      >
                        <Edit3 className="w-3.5 h-3.5" />
                        Uzupełnij ręcznie
                      </button>
                      <span className="text-aurora-subtle">lub</span>
                      <button
                        onClick={() => handleNexusResearch({ force: hasResearch })}
                        disabled={isResearching}
                        className="text-sm text-purple-400 hover:text-purple-300 flex items-center gap-1 disabled:opacity-50"
                      >
                        <Sparkles className="w-3.5 h-3.5" />
                        Generuj z NEXUS AI
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <>
                  {profile?.mission && (
                    <StrategyCard
                      icon={Target}
                      color="text-blue-400"
                      label="Misja"
                      value={profile.mission}
                      zrodlo={wpisZrodla(zrodla, 'mission')}
                    />
                  )}
                  {profile?.goals && (
                    <StrategyCard
                      icon={Rocket}
                      color="text-emerald-400"
                      label="Cele strategiczne"
                      value={profile.goals}
                      zrodlo={wpisZrodla(zrodla, 'goals')}
                    />
                  )}
                  {profile?.differentiators && (
                    <StrategyCard
                      icon={Star}
                      color="text-amber-400"
                      label="Wyróżniki"
                      value={profile.differentiators}
                      zrodlo={wpisZrodla(zrodla, 'differentiators')}
                    />
                  )}
                  {profile?.analysis && (
                    <StrategyCard
                      icon={BarChart3}
                      color="text-cyan-400"
                      label="Analiza"
                      value={profile.analysis}
                      zrodlo={wpisZrodla(zrodla, 'analysis')}
                    />
                  )}
                  {profile?.policies && (
                    <StrategyCard
                      icon={ShieldCheck}
                      color="text-purple-400"
                      label="Polityka"
                      value={profile.policies}
                      zrodlo={wpisZrodla(zrodla, 'policies')}
                    />
                  )}
                  {profile?.assets_description && (
                    <StrategyCard
                      icon={Package}
                      color="text-orange-400"
                      label="Zasoby"
                      value={profile.assets_description}
                      zrodlo={wpisZrodla(zrodla, 'assets_description')}
                    />
                  )}
                  {profile?.cost_structure && (
                    <StrategyCard
                      icon={Coins}
                      color="text-rose-400"
                      label="Struktura kosztów"
                      value={profile.cost_structure}
                      zrodlo={wpisZrodla(zrodla, 'cost_structure')}
                    />
                  )}
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* Unit Form Modal.
          Mounted only while open and keyed per target, so its state initialisers
          re-run for every unit instead of freezing on the first mount. */}
      {showUnitForm && (
        <UnitFormModal
          key={editingUnit?.id ?? `new-${addParentId ?? 'root'}-${addDefaultType}`}
          onClose={() => {
            setShowUnitForm(false);
            setEditingUnit(null);
          }}
          onSave={handleSaveUnit}
          editingUnit={editingUnit}
          parentId={addParentId}
          defaultType={addDefaultType}
        />
      )}
    </div>
  );
}

// ─── Helper Components ────────────────────────────────────────────────────

function QuickStat({
  label,
  value,
  icon: Icon,
  color,
}: {
  label: string;
  value: number;
  icon: typeof Building2;
  color: string;
}) {
  return (
    <div className="text-center">
      <div className="mono text-2xl font-bold text-aurora-text tabular-nums">{value}</div>
      <div className={`text-xs ${color} flex items-center justify-center gap-1 mt-0.5`}>
        <Icon className="w-3 h-3" />
        {label}
      </div>
    </div>
  );
}

function ProfileField({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
}) {
  return (
    <div className="field">
      <label className="field__label">{label}</label>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={3}
        className="textarea text-sm resize-none"
        placeholder={placeholder}
      />
    </div>
  );
}

function StrategyCard({
  icon: Icon,
  color,
  label,
  value,
  zrodlo,
}: {
  icon: typeof Target;
  color: string;
  label: string;
  value: string;
  zrodlo?: ReturnType<typeof wpisZrodla>;
}) {
  return (
    <div className="bg-aurora-bg-subtle border border-aurora-border rounded-ids-lg p-4">
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`w-4 h-4 ${color}`} />
        <p className="overline">{label}</p>
        {zrodlo && <ZrodloChip zrodla={{ [label]: zrodlo }} pole={label} inline />}
      </div>
      <p className="text-sm text-aurora-text leading-relaxed whitespace-pre-line">{value}</p>
    </div>
  );
}

/**
 * „nie zbadano" — JEDEN wspólny napis na pole bez źródła i bez wartości.
 * Nie „brak danych o produktach": to zdanie brzmi jak wynik badania, którego
 * nie było (ta sama zasada, co „nie podaje ≠ 0" w panelu produkcyjnym).
 */
function NieZbadano({ inline = false }: { inline?: boolean }) {
  const Tag = inline ? 'span' : 'p';
  return (
    <Tag className="text-sm text-aurora-subtle italic" data-testid="nie-zbadano">
      nie zbadano
    </Tag>
  );
}

/**
 * Pole tekstowe profilu z trzema stanami — jak „nie podaje / 0 / >0" w pulsie
 * panelu: treść (z chipem pochodzenia), „nie zbadano" (bez źródła i bez
 * wartości), albo „—" (zbadane, a puste — np. firma nie ma strony www).
 */
function PoleTekstowe({
  zrodla,
  pole,
  wartosc,
}: {
  zrodla: Zrodla;
  pole: string;
  wartosc: string | undefined;
}) {
  if (czyNieZbadano(zrodla, pole, wartosc)) return <NieZbadano />;
  return (
    <>
      {wartosc?.trim() ? (
        <p className="text-sm text-aurora-muted leading-relaxed">{wartosc}</p>
      ) : (
        <p className="text-sm text-aurora-subtle" aria-label="zbadane, puste">
          —
        </p>
      )}
      <ZrodloChip zrodla={zrodla} pole={pole} />
    </>
  );
}

function tytulZrodla(zrodla: Zrodla, pole: string): string | undefined {
  const wpis = wpisZrodla(zrodla, pole);
  if (!wpis) return undefined;
  const data = dataZapisu(wpis);
  return `${etykietaZrodla(wpis.zrodlo)}${data ? ` · ${data}` : ''}`;
}

/** Chip pochodzenia pola: „z wywiadu · 13.09.2026". Nic, gdy pole nie ma wpisu. */
function ZrodloChip({
  zrodla,
  pole,
  inline = false,
}: {
  zrodla: Zrodla;
  pole: string;
  inline?: boolean;
}) {
  const wpis = wpisZrodla(zrodla, pole);
  if (!wpis) return null;
  const data = dataZapisu(wpis);
  const tresc = `${etykietaZrodla(wpis.zrodlo)}${data ? ` · ${data}` : ''}`;
  return (
    <span
      className={`badge border-transparent bg-aurora-bg-subtle text-aurora-subtle font-medium ${
        inline ? 'ml-auto' : 'mt-3'
      }`}
      data-testid="zrodlo-pola"
      data-pole={pole}
      title={wpis.zrodlo_url ? wpis.zrodlo_url : undefined}
    >
      {tresc}
    </span>
  );
}
